import React, { useCallback, useEffect, useRef, useState, memo } from 'react'
import './index.css'
import MediaItem from '../MediaItem'
import { useAllLazyQuery } from '../../graphql'
import LoadingMediaItems from '../LoadingMediaItems'
import LoadingIndicator from '../LoadingIndicator'

const MemoizedMediaItem = memo(({ item, onClick }: { item: MediaItem; onClick: () => void }) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        willChange: 'transform',
        transform: 'translateZ(0)',
      }}
      onClick={onClick}>
      <MediaItem item={item} />
    </div>
  )
})
MemoizedMediaItem.displayName = 'MemoizedMediaItem'

type ExploreAllProps = {
  open: boolean
  closeExploreAll: (e: React.MouseEvent<HTMLSpanElement, MouseEvent> | undefined) => void
  id: string | null
  catalogId: string | null
  search?: string | null
}

const ExploreAll = (props: ExploreAllProps) => {
  const { open, closeExploreAll, id, catalogId, search } = props

  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [mobile, setMobile] = useState(window.innerWidth < 950)
  const [items, setItems] = useState<MediaItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isStable, setIsStable] = useState(false)
  const [reachedEnd, setReachedEnd] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const gridContainerRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef(false)

  const [fetchAll] = useAllLazyQuery({
    fetchPolicy: 'network-only',
  })

  const getGridConfig = useCallback(() => {
    const width = window.innerWidth
    const modalWidth = mobile ? width * 0.92 : width * 0.96

    // Calculate optimal item width (accounting for gap)
    const baseItemWidth = 330 // base width close to MediaItem width (332px)
    const mobileItemWidth = 160 // mobile item width
    const gapWidth = 20

    let columns
    let itemWidth

    if (width < 650) {
      columns = 2
      itemWidth = Math.min(modalWidth / columns - gapWidth, mobileItemWidth)
    } else if (width < 801) {
      columns = 3
      itemWidth = Math.min(modalWidth / columns - gapWidth, mobileItemWidth)
    } else if (width < 1148) {
      columns = 2
      itemWidth = Math.min(modalWidth / columns - gapWidth, baseItemWidth)
    } else if (width < 1524) {
      columns = 3
      itemWidth = Math.min(modalWidth / columns - gapWidth, baseItemWidth)
    } else if (width < 1905) {
      columns = 4
      itemWidth = Math.min(modalWidth / columns - gapWidth, baseItemWidth)
    } else {
      columns = 5
      itemWidth = Math.min(modalWidth / columns - gapWidth, baseItemWidth)
    }

    return { columns, itemWidth }
  }, [mobile])

  const [gridConfig, setGridConfig] = useState(getGridConfig())

  const loadData = useCallback(
    async (skipCount = 0) => {
      if (loadingRef.current || !isOpen) return
      loadingRef.current = true
      setLoading(true)
      setIsStable(false)

      // Store the current scroll position before loading
      const scrollPosition = gridContainerRef.current?.scrollTop || 0

      try {
        const queryId = id || catalogId
        if (!queryId) return

        const variables = {
          rowData: {
            id: queryId,
          },
          itemData: {
            skip: skipCount,
            take: 100,
            search,
          },
        }

        const res = await fetchAll({ variables })
        const itemsRes = res?.data?.all?.row?.items
        const rowTitle = res?.data?.all?.row?.title

        if (itemsRes && isOpen) {
          // Check if we actually got new items
          const newItems = itemsRes.list || []
          const hasNewItems = newItems.length > 0

          // Only update if we have new items or this is the initial load
          if (skipCount === 0 || hasNewItems) {
            if (skipCount === 0) {
              setItems(newItems)
            } else {
              setItems((prev) => [...prev, ...newItems])
            }
            setTitle(rowTitle || 'Explore All')
          }

          // Important: Only set hasMore to true if we actually got new items
          const hasMoreContent = itemsRes.hasMore && hasNewItems
          setHasMore(hasMoreContent)

          // Track if we've reached the end of all content
          setReachedEnd(!hasMoreContent && skipCount > 0)

          // Wait for the DOM to update before restoring scroll position
          setTimeout(() => {
            if (gridContainerRef.current && skipCount > 0) {
              gridContainerRef.current.scrollTop = scrollPosition
            }
            setIsStable(true)
          }, 50)
        }
      } finally {
        loadingRef.current = false
        setLoading(false)
      }
    },
    [id, catalogId, search, fetchAll, isOpen],
  )

  useEffect(() => {
    if (open) {
      setIsOpen(true)
      document.body.style.overflowY = 'hidden'
      if (id || catalogId) {
        setItems([])
        loadData(0)
      }
    } else {
      setIsOpen(false)
      document.body.style.overflowY = 'scroll'
    }
  }, [open, id, catalogId, loadData])

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 950
      setMobile(isMobile)
      setGridConfig(getGridConfig())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [getGridConfig])

  useEffect(() => {
    return () => {
      document.body.style.overflowY = 'scroll'
    }
  }, [])

  useEffect(() => {
    if (!gridContainerRef.current || !isStable || !isOpen) return

    // Throttle function to prevent excessive calls
    let ticking = false
    let lastScrollPosition = 0
    let lastLoadTime = 0

    // Lower threshold when we're near the end to preload more content
    // This prevents the appearance of flickering at the bottom
    const loadThreshold = 0.9
    // Minimum time between load operations
    const minLoadInterval = 1000 // 1 second

    const handleScroll = () => {
      if (!hasMore || loadingRef.current || !gridContainerRef.current) return

      const { scrollTop, scrollHeight, clientHeight } = gridContainerRef.current
      lastScrollPosition = scrollTop

      // Check if we're approaching the end of content
      const approachingEnd = lastScrollPosition > (scrollHeight - clientHeight) * loadThreshold
      const canLoadMore = Date.now() - lastLoadTime > minLoadInterval

      if (!ticking && approachingEnd && canLoadMore) {
        window.requestAnimationFrame(() => {
          lastLoadTime = Date.now()
          loadData(items.length)
          ticking = false
        })
        ticking = true
      }
    }

    const container = gridContainerRef.current
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [hasMore, items.length, loadData, isStable, isOpen])

  useEffect(() => {
    if (open) {
      setReachedEnd(false)
    }
  }, [open])

  const handleOnClose = useCallback(() => {
    setIsOpen(false)
    if (closeExploreAll) closeExploreAll(undefined)
  }, [closeExploreAll])

  const modalStyle: React.CSSProperties = {
    ...styles.exploreAllModal,
    border: isOpen ? '1px solid #404040' : 'none',
    height: isOpen ? '97%' : '0%',
    width: mobile ? '92%' : '96%',
    borderTopLeftRadius: mobile ? 10 : 25,
    borderTopRightRadius: mobile ? 10 : 25,
    transform: 'translateZ(0)',
  }

  const gridContainerStyle: React.CSSProperties = {
    ...styles.exploreAllMediaItemContainer,
    height: 'calc(100% - 160px)',
    overflow: 'auto',
    padding: '0',
    position: 'relative',
    WebkitOverflowScrolling: 'touch',
    willChange: 'transform',
    transform: 'translateZ(0)',
    WebkitTransform: 'translateZ(0)',
    scrollBehavior: 'smooth',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${gridConfig.columns}, ${gridConfig.itemWidth}px)`,
    columnGap: '20px',
    rowGap: '20px',
    padding: '20px',
    boxSizing: 'border-box',
    willChange: 'transform',
    backfaceVisibility: 'hidden',
    margin: '0 auto',
    justifySelf: 'center',
    justifyItems: 'center',
  }

  // Recalculate container width to ensure even margins
  const totalGridWidth = gridConfig.columns * gridConfig.itemWidth + (gridConfig.columns - 1) * 20
  const gridContainerWidth = Math.min(totalGridWidth + 40, window.innerWidth * 0.9)

  const gridWrapperStyle: React.CSSProperties = {
    width: gridContainerWidth,
    margin: '0 auto',
    maxWidth: '100%',
  }

  // Memoize the grid items to prevent unnecessary re-renders
  const renderMediaItems = useCallback(() => {
    return items.map((item) => (
      <MemoizedMediaItem key={`item-${item.id || Math.random()}`} item={item} onClick={handleOnClose} />
    ))
  }, [items, handleOnClose])

  return (
    <div className="exploreAllModal" ref={containerRef} style={modalStyle}>
      <div style={styles.exploreAllCloseContainer}>
        <div className="exploreAllClose" onClick={handleOnClose} />
      </div>

      <span className="exploreAllTitle" style={styles.exploreAllTitle}>
        {title}
      </span>

      <div ref={gridContainerRef} style={gridContainerStyle}>
        {loading && items.length === 0 && <LoadingMediaItems />}

        {isOpen && items.length > 0 && (
          <>
            <div style={gridWrapperStyle}>
              <div style={gridStyle}>{renderMediaItems()}</div>
            </div>
            <div style={{ marginBottom: 10 }}>{hasMore && !reachedEnd && <LoadingIndicator />}</div>
            {reachedEnd}
          </>
        )}

        {isOpen && !loading && items.length === 0 && (
          <div style={{ padding: '20px', textAlign: 'center', color: 'white' }}> No items found </div>
        )}
      </div>
    </div>
  )
}

const styles = {
  exploreAllModal: {
    display: 'flex',
    position: 'fixed',
    height: '0%',
    left: 0,
    right: 0,
    bottom: 0,
    margin: '0 auto',
    overflowY: 'hidden',
    overflowX: 'hidden',
    background: '0% 0% no-repeat padding-box padding-box rgb(38, 38, 38, 0.9)',
    boxShadow: '0px 6px 25px #000000BF',
    opacity: 1,
    backdropFilter: 'blur(30px)',
    WebkitBackdropFilter: 'blur(30px)',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    zIndex: 10,
    transition: 'all .3s ease-in-out',
    transform: 'translateZ(0)',
    willChange: 'transform',
  } as React.CSSProperties,
  exploreAllCloseContainer: {
    zIndex: 15,
    position: 'sticky',
    width: '100%',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'end',
  } as React.CSSProperties,
  exploreAllTitle: {
    textAlign: 'left',
    fontSize: 38,
    letterSpacing: 0,
    color: '#FFFFFF',
    opacity: 1,
    fontWeight: 700,
    padding: '63px 0 35px 0',
    alignSelf: 'center',
  } as React.CSSProperties,
  exploreAllMediaItemContainer: {
    width: '100%',
    display: 'flex',
    position: 'relative',
    padding: 0,
  } as React.CSSProperties,
  exploreAllText: {
    color: 'white',
    fontWeight: 700,
    width: '100%',
    marginBottom: 20,
  } as React.CSSProperties,
}

export default ExploreAll
